import {
  isLikelyLocalFilesystemPath,
  resolveAttachmentDisplayURL,
  resolveBrowserAttachmentURL,
  type AttachmentFileRef,
  type AttachmentDisplayURLParams,
} from '@/utils/agent-file-url';

export type ConversationAttachmentRef = AttachmentFileRef & {
  sourceSessionId?: string | null;
  source_session_id?: string | null;
  expiresAt?: number;
  expires_at?: number;
};

export type ConversationAttachmentContent = {
  url?: string | null;
  displayURL?: string | null;
  displayUrl?: string | null;
  display_url?: string | null;
  downloadURL?: string | null;
  downloadUrl?: string | null;
  download_url?: string | null;
  thumbnailURL?: string | null;
  thumbnailUrl?: string | null;
  thumbnail_url?: string | null;
  publicShareURL?: string | null;
  publicShareUrl?: string | null;
  public_share_url?: string | null;
  authRequired?: boolean | null;
  auth_required?: boolean | null;
  authScope?: string | null;
  auth_scope?: string | null;
  fileRef?: ConversationAttachmentRef | null;
  fileRefs?: ReadonlyArray<ConversationAttachmentRef> | null;
  fileName?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
};

export type ConversationAttachment = {
  content: ConversationAttachmentContent;
  displaySource: string | null;
  fileName: string;
  fileRef?: ConversationAttachmentRef;
  fileRefs?: ReadonlyArray<ConversationAttachmentRef>;
  url?: string;
  sourcePath?: string;
  environmentId?: string;
  nativePreviewSessionId?: string;
  agentId?: string;
  signature?: string;
  mimeType?: string;
  sizeBytes?: number;
  displayParams: AttachmentDisplayURLParams;
};

function normalizedRefPath(ref: ConversationAttachmentRef | undefined): string {
  return ref?.path?.trim() ?? '';
}

function refCanonicalURL(ref: ConversationAttachmentRef | undefined): string {
  return (
    ref?.displayURL ??
    ref?.displayUrl ??
    ref?.display_url ??
    ref?.publicShareURL ??
    ref?.publicShareUrl ??
    ref?.public_share_url ??
    ref?.downloadURL ??
    ref?.downloadUrl ??
    ref?.download_url ??
    ''
  ).trim();
}

function refKey(ref: ConversationAttachmentRef) {
  return [
    ref.refId?.trim() ?? '',
    ref.ref_id?.trim() ?? '',
    refCanonicalURL(ref),
    normalizedRefPath(ref),
    ref.environmentId?.trim() ?? '',
    ref.sourceSessionId?.trim() ?? '',
    ref.agentId?.trim() ?? '',
    ref.signature?.trim() ?? '',
  ].join('\u0000');
}

export function conversationAttachmentRefs(
  content: ConversationAttachmentContent
): ConversationAttachmentRef[] {
  const refs: ConversationAttachmentRef[] = [];
  const seen = new Set<string>();
  const addRef = (ref: ConversationAttachmentRef | undefined) => {
    const path = normalizedRefPath(ref);
    if (!ref || (!path && !refCanonicalURL(ref) && !ref.refId && !ref.ref_id))
      return;
    const normalized = {
      ...ref,
      path,
      refId: ref.refId ?? ref.ref_id ?? undefined,
      kind: ref.kind ?? undefined,
      source: ref.source ?? undefined,
      environmentId: ref.environmentId ?? ref.environment_id ?? undefined,
      sourceSessionId:
        ref.sourceSessionId ?? ref.source_session_id ?? undefined,
      agentId: ref.agentId ?? ref.agent_id ?? undefined,
      signature: ref.signature ?? undefined,
      displayURL: ref.displayURL ?? ref.displayUrl ?? ref.display_url,
      downloadURL: ref.downloadURL ?? ref.downloadUrl ?? ref.download_url,
      thumbnailURL: ref.thumbnailURL ?? ref.thumbnailUrl ?? ref.thumbnail_url,
      publicShareURL:
        ref.publicShareURL ?? ref.publicShareUrl ?? ref.public_share_url,
      authRequired: ref.authRequired ?? ref.auth_required,
      authScope: ref.authScope ?? ref.auth_scope ?? undefined,
      name: ref.name ?? undefined,
      mimeType: ref.mimeType ?? ref.mime_type ?? undefined,
      sizeBytes: ref.sizeBytes ?? ref.size_bytes ?? undefined,
      contentHash: ref.contentHash ?? ref.content_hash ?? undefined,
      expiresAt: ref.expiresAt ?? ref.expires_at ?? undefined,
    };
    const key = refKey(normalized);
    if (seen.has(key)) return;
    seen.add(key);
    refs.push(normalized);
  };

  addRef(content.fileRef ?? undefined);
  for (const ref of content.fileRefs ?? []) {
    addRef(ref);
  }
  return refs;
}

export function isConversationWorkspaceRef(
  ref: ConversationAttachmentRef | undefined
): boolean {
  const path = normalizedRefPath(ref);
  const environmentId = ref?.environmentId?.trim().toLowerCase() ?? '';
  return (
    environmentId === 'vfs' ||
    environmentId === 'agent-vfs' ||
    path.startsWith('/.willow/') ||
    path.startsWith('.willow/')
  );
}

function isDisplayableConversationRef(
  ref: ConversationAttachmentRef | undefined
): boolean {
  const path = normalizedRefPath(ref);
  if (!ref) return false;
  if (refCanonicalURL(ref)) return true;
  if (isConversationWorkspaceRef(ref)) return true;
  if (!path || isLikelyLocalFilesystemPath(path)) return false;
  return false;
}

export function selectConversationDisplayRef(
  content: ConversationAttachmentContent
): ConversationAttachmentRef | undefined {
  const refs = conversationAttachmentRefs(content);
  return refs.find(isDisplayableConversationRef) ?? refs[0];
}

export function selectConversationPreviewRef(
  content: ConversationAttachmentContent
): ConversationAttachmentRef | undefined {
  const refs = conversationAttachmentRefs(content);
  return (
    refs.find(ref => !isConversationWorkspaceRef(ref)) ??
    selectConversationDisplayRef(content)
  );
}

export function conversationAttachmentFileName(
  content: ConversationAttachmentContent,
  fallback: string
): string {
  const explicitName = content.fileName?.trim();
  if (explicitName) return explicitName;

  const source =
    selectConversationPreviewRef(content)?.path ??
    selectConversationDisplayRef(content)?.path ??
    content.url;
  const fileName = source?.split('/').findLast(Boolean)?.trim();
  return fileName || fallback;
}

export function resolveConversationAttachment(
  content: ConversationAttachmentContent,
  fallbackFileName = 'attachment'
): ConversationAttachment {
  const displayRef = selectConversationDisplayRef(content);
  const previewRef = selectConversationPreviewRef(content) ?? displayRef;
  const fileRefs = conversationAttachmentRefs(content);
  const url = content.url?.trim() || undefined;
  const displayParams: AttachmentDisplayURLParams = {
    src: url,
    displayURL: content.fileRef?.displayURL ?? content.displayURL,
    displayUrl: content.fileRef?.displayUrl ?? content.displayUrl,
    display_url: content.fileRef?.display_url ?? content.display_url,
    downloadURL: content.fileRef?.downloadURL ?? content.downloadURL,
    downloadUrl: content.fileRef?.downloadUrl ?? content.downloadUrl,
    download_url: content.fileRef?.download_url ?? content.download_url,
    thumbnailURL: content.fileRef?.thumbnailURL ?? content.thumbnailURL,
    thumbnailUrl: content.fileRef?.thumbnailUrl ?? content.thumbnailUrl,
    thumbnail_url: content.fileRef?.thumbnail_url ?? content.thumbnail_url,
    publicShareURL: content.fileRef?.publicShareURL ?? content.publicShareURL,
    publicShareUrl: content.fileRef?.publicShareUrl ?? content.publicShareUrl,
    public_share_url:
      content.fileRef?.public_share_url ?? content.public_share_url,
    authRequired: content.fileRef?.authRequired ?? content.authRequired,
    auth_required: content.fileRef?.auth_required ?? content.auth_required,
    authScope: content.fileRef?.authScope ?? content.authScope,
    auth_scope: content.fileRef?.auth_scope ?? content.auth_scope,
    filePath: displayRef?.path,
    environmentId: displayRef?.environmentId,
    agentId: displayRef?.agentId,
    signature: displayRef?.signature,
    fileRefs,
  };

  return {
    content,
    displaySource: resolveAttachmentDisplayURL(displayParams),
    fileName: conversationAttachmentFileName(content, fallbackFileName),
    fileRef: previewRef,
    fileRefs,
    url,
    sourcePath: previewRef?.path ?? undefined,
    environmentId: previewRef?.environmentId ?? undefined,
    nativePreviewSessionId:
      previewRef?.sourceSessionId ??
      fileRefs.find(ref => ref.sourceSessionId)?.sourceSessionId ??
      undefined,
    agentId: previewRef?.agentId ?? undefined,
    signature: previewRef?.signature ?? undefined,
    mimeType: content.mimeType ?? undefined,
    sizeBytes: content.sizeBytes ?? undefined,
    displayParams,
  };
}

export function hasConversationAttachmentSource(
  content: ConversationAttachmentContent
): boolean {
  const attachment = resolveConversationAttachment(content);
  return Boolean(
    attachment.displaySource ||
    attachment.sourcePath ||
    resolveBrowserAttachmentURL({ src: content.url })
  );
}
